import { Injectable, inject, signal, computed } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { Roles } from '@core/constants/roles.constants';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionStatus,
  SilentRequest,
} from '@azure/msal-browser';
import {
  Observable,
  Subject,
  from,
  map,
  filter,
  takeUntil,
  catchError,
  of,
  switchMap,
  delay,
  retry,
  throwError,
} from 'rxjs';
import { environment } from '@environments/environment';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly http = inject(HttpClient);
  private readonly destroying$ = new Subject<void>();

  // Signals for reactive state management
  readonly isAuthenticated = signal<boolean>(false);
  readonly currentUser = signal<AccountInfo | null>(null);
  readonly roles = signal<string[]>([]);
  readonly userProfilePhoto = signal<string | null>(null);
  moodleFormationUrl = signal<string | null>(null);
  // Computed property for checking if user has admin role
  readonly isAdmin = computed(() => this.roles().includes(Roles.Admin));
  readonly hasGestRole = computed(
    () =>
      this.roles().filter((gest) => gest.startsWith(Roles.Gest_)).length > 0,
  );

  constructor() {
    // Initialize authentication state
    this.updateAuthState();

    // Subscribe to auth state changes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this.destroying$),
      )
      .subscribe(() => {
        this.updateAuthState();
        // Load profile photo after MSAL operations are complete
        if (this.isAuthenticated()) {
          // Add a small delay to ensure MSAL is fully ready
          setTimeout(() => {
            this.loadUserProfilePhoto().subscribe();
          }, 500);
        }
      });

    // Handle redirect after login
    this.msalService.handleRedirectObservable().subscribe();
  }

  login(): Observable<AuthenticationResult> {
    return from(
      this.msalService.loginPopup({
        scopes: environment.azureAd.scopes,
      }),
    ).pipe(
      map((response) => {
        this.msalService.instance.setActiveAccount(response.account);
        this.updateAuthState();
        // Load profile photo after successful login with a delay
        setTimeout(() => {
          this.loadUserProfilePhoto().subscribe();
        }, 1000);
        return response;
      }),
    );
  }

  logout(): Observable<void> {
    return from(this.msalService.logout()).pipe(
      map(() => {
        // Reset signals on logout
        this.isAuthenticated.set(false);
        this.currentUser.set(null);
        this.roles.set([]);
        this.userProfilePhoto.set(null);
      }),
    );
  }

  acquireToken(scopes: string[]): Observable<string> {
    const account = this.msalService.instance.getActiveAccount();
    const request: SilentRequest = {
      account: account ?? undefined,
      scopes: scopes,
    };

    // Try silent token acquisition first
    return from(this.msalService.acquireTokenSilent(request)).pipe(
      catchError(() => {
        // If silent acquisition fails, try popup
        return from(this.msalService.acquireTokenPopup(request));
      }),
      map((response) => response.accessToken),
    );
  }

  // Method to check if MSAL is ready
  private isMsalReady(): boolean {
    try {
      // Try to get accounts to check if MSAL is initialized
      this.msalService.instance.getAllAccounts();
      return true;
    } catch (error) {
      return false;
    }
  }

  // Method to acquire token specifically for Microsoft Graph
  private acquireGraphToken(): Observable<string> {
    // Check if MSAL is ready
    if (!this.isMsalReady()) {
      return of(null).pipe(
        delay(1000),
        switchMap(() => this.acquireGraphToken()),
      );
    }

    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return throwError(() => new Error('No active account found'));
    }

    const graphRequest: SilentRequest = {
      account: account,
      scopes: ['https://graph.microsoft.com/User.Read'],
    };

    return from(this.msalService.acquireTokenSilent(graphRequest)).pipe(
      catchError((silentError) => {
        return from(this.msalService.acquireTokenPopup(graphRequest));
      }),
      map((response) => response.accessToken),
      retry({
        count: 2,
        delay: 1000,
      }),
    );
  }

  // Updated method to get user profile photo with better error handling
  loadUserProfilePhoto(): Observable<string | null> {
    // Don't try to load photo if not authenticated
    if (!this.isAuthenticated()) {
      return of(null);
    }

    return this.acquireGraphToken().pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('Failed to acquire Graph token');
        }

        const headers = {
          Authorization: `Bearer ${token}`,
        };

        // Call Microsoft Graph API to get user photo
        return this.http
          .get('https://graph.microsoft.com/v1.0/me/photo/$value', {
            headers,
            responseType: 'blob',
          })
          .pipe(
            switchMap((blob: Blob) => {
              // Convert blob to data URL
              return new Observable<string>((observer) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const dataUrl = reader.result as string;
                  this.userProfilePhoto.set(dataUrl);
                  observer.next(dataUrl);
                  observer.complete();
                };
                reader.onerror = () => {
                  console.error('Failed to read profile photo blob');
                  this.userProfilePhoto.set(null);
                  observer.error('Failed to read profile photo');
                };
                reader.readAsDataURL(blob);
              });
            }),
            catchError((error) => {
              if (error.status === 404) {
              } else {
              }
              this.userProfilePhoto.set(null);
              return of(null);
            }),
          );
      }),
      catchError((error) => {
        console.error(
          'Failed to acquire Graph token for profile photo:',
          error,
        );
        this.userProfilePhoto.set(null);
        return of(null);
      }),
    );
  }

  getRoles(): string[] {
    const account = this.msalService.instance.getActiveAccount();
    if (account?.idTokenClaims) {
      return (account.idTokenClaims['roles'] as string[]) || [];
    }
    return [];
  }

  getActiveAccount(): AccountInfo | null {
    return this.msalService.instance.getActiveAccount();
  }

  isProdEnvironment = (): boolean => environment.production;
  getEnvironmentLabel() {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return 'LOCAL';
    else if (!this.isProdEnvironment()) return 'UAT';
    else return null;
  }

  getMoodleFormationStaticUrl(): string {
    // return (
    //   'https://preprod.cnfpc.cblue.be' + '/blocks/cblue_catalogue/details.php?courseid='
    // );
    return environment.moodle.baseUrl + environment.moodle.moodleFormationIdUrl;
    //return environment.moodle.baseUrl + environment.moodle.moodleFormationIdUrl;
  }

  getMoodleCourseViewStaticUrl(): string {
    return environment.moodle.baseUrl + '/course/view.php?id=';
  }

  private updateAuthState(): void {
    let account = this.msalService.instance.getActiveAccount();

    // Fallback: set the first account if none is active
    if (!account && this.msalService.instance.getAllAccounts().length > 0) {
      account = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(account);
    }

    this.isAuthenticated.set(!!account);
    this.currentUser.set(account);

    // Extract roles from token claims
    const claims = account?.idTokenClaims as any;
    const roles = claims?.roles || [];

    this.roles.set(roles);
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }
}
